import React, { useEffect, useMemo, useState } from "react";
import "./admin-management.scss";
import { getPermissions, getRoles, getStaff } from "../../api/crawlerApi";
import { extractList } from "../../api/client";

const tabs = ["Staff", "Roles", "Permissions"];

const CheckIcon = ({ enabled }) => (
  <span className={`admin-management__permission ${enabled ? "admin-management__permission--yes" : "admin-management__permission--no"}`}>
    {enabled ? "✓" : "—"}
  </span>
);

const inferPermissionGrid = (items) =>
  items.map((item) => ({
    module: item.display_name || item.name,
    view: /^view[-_]/.test(item.name),
    create: /^create[-_]/.test(item.name),
    update: /^edit[-_]|update[-_]/.test(item.name),
    delete: /^delete[-_]/.test(item.name),
  }));

const AdminManagementPage = () => {
  const [activeTab, setActiveTab] = useState("Staff");
  const [staff, setStaff] = useState([]);
  const [roles, setRoles] = useState([]);
  const [permissions, setPermissions] = useState([]);
  const [search, setSearch] = useState("");
  const [error, setError] = useState("");

  useEffect(() => {
    Promise.all([getStaff(), getRoles(), getPermissions()])
      .then(([staffResponse, rolesResponse, permissionsResponse]) => {
        setStaff(extractList(staffResponse));
        setRoles(extractList(rolesResponse));
        setPermissions(extractList(permissionsResponse));
      })
      .catch((err) => {
        setError(err.message || "Failed to load administrative data.");
      });
  }, []);

  const filteredStaff = useMemo(() => {
    const keyword = search.trim().toLowerCase();
    if (!keyword) return staff;

    return staff.filter((person) =>
      [person.name, person.email, person.role_display_name, person.role_name]
        .filter(Boolean)
        .some((value) => String(value).toLowerCase().includes(keyword))
    );
  }, [search, staff]);

  const permissionRows = useMemo(() => inferPermissionGrid(permissions), [permissions]);

  return (
    <div className="admin-management">
      <section className="admin-management__hero">
        <div>
          <span>Administrative Management</span>
          <h2>Staff, roles & permissions</h2>
          <p>Control user access for YKMW Crawler modules from one place.</p>
        </div>
      </section>

      <section className="admin-management__summary">
        <article><span>Total Staff</span><strong>{staff.length}</strong><small>Loaded from API</small></article>
        <article><span>Roles</span><strong>{roles.length}</strong><small>Role groups configured</small></article>
        <article><span>Permission Sets</span><strong>{permissions.length}</strong><small>Current access keys</small></article>
        <article><span>Pending Invites</span><strong>0</strong><small>Invitation flow not wired yet</small></article>
      </section>

      <section className="admin-management__panel">
        <div className="admin-management__toolbar">
          <div className="admin-management__tabs">
            {tabs.map((tab) => (
              <button
                key={tab}
                type="button"
                className={activeTab === tab ? "admin-management__tab--active" : ""}
                onClick={() => setActiveTab(tab)}
              >
                {tab}
              </button>
            ))}
          </div>
          <input
            type="search"
            placeholder={`Search ${activeTab.toLowerCase()}...`}
            value={search}
            onChange={(event) => setSearch(event.target.value)}
          />
        </div>

        {error && <div>{error}</div>}

        {activeTab === "Staff" && (
          <div className="admin-management__table-wrap">
            <table>
              <thead>
                <tr><th>Name</th><th>Email</th><th>Role</th><th>Status</th><th>Last Login</th><th>Action</th></tr>
              </thead>
              <tbody>
                {filteredStaff.length === 0 ? (
                  <tr><td colSpan="6">No staff found.</td></tr>
                ) : (
                  filteredStaff.map((person) => (
                    <tr key={person.id}>
                      <td><strong>{person.name}</strong></td>
                      <td>{person.email || "-"}</td>
                      <td>{person.role_display_name || person.role_name || "-"}</td>
                      <td><span className={`admin-management__status admin-management__status--${Number(person.status) === 1 ? "active" : "inactive"}`}>{Number(person.status) === 1 ? "Active" : "Inactive"}</span></td>
                      <td>{person.last_logged_in || "Pending"}</td>
                      <td><button className="admin-management__link-button" type="button">View</button></td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        )}

        {activeTab === "Roles" && (
          <div className="admin-management__role-grid">
            {roles.map((item) => (
              <article key={item.id || item.name}>
                <div>
                  <h3>{item.display_name || item.name}</h3>
                  <span>{item.staff_count || 0} users</span>
                </div>
                <p>{item.description || "No description provided."}</p>
              </article>
            ))}
          </div>
        )}

        {activeTab === "Permissions" && (
          <div className="admin-management__table-wrap">
            <table>
              <thead>
                <tr><th>Module</th><th>View</th><th>Create</th><th>Update</th><th>Delete</th></tr>
              </thead>
              <tbody>
                {permissionRows.map((item) => (
                  <tr key={item.module}>
                    <td><strong>{item.module}</strong></td>
                    <td><CheckIcon enabled={item.view} /></td>
                    <td><CheckIcon enabled={item.create} /></td>
                    <td><CheckIcon enabled={item.update} /></td>
                    <td><CheckIcon enabled={item.delete} /></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </section>
    </div>
  );
};

export default AdminManagementPage;
